import { useState } from 'react';

import {
  FLOWS,
  FLOW_LABELS,
  LAYOUTS,
  LAYOUT_LABELS,
  Flow,
  Layout,
  Emails,
  EmailLayouts,
  EmailDraft,
  Settings,
} from './types';
import generateEmailHtml from './generateEmailHtml';
import useCopyToClipboard from './useCopyToClipboard';

type EmailCardProps = {
  index: number;
  draft: EmailDraft | null;
  layout: Layout;
  settings: Settings;
  flow: Flow;
  onClick: () => void;
  onLayoutChange: (layout: Layout) => void;
};

function EmailCard({
  index,
  draft,
  layout,
  settings,
  flow,
  onClick,
  onLayoutChange,
}: EmailCardProps) {
  const { copy, copied, error } = useCopyToClipboard();

  let copyLabel = 'Copy';

  if (copied) {
    copyLabel = 'Copied!';
  } else if (error) {
    copyLabel = 'Copy failed';
  }

  return (
    <div className="eg-email-card">
      <button type="button" className="eg-email-card-body" onClick={onClick}>
        <div className="eg-email-card-header">
          <span className="eg-email-card-number">{`Email ${index + 1}`}</span>
          <span className={`eg-badge ${draft ? 'eg-badge-generated' : 'eg-badge-empty'}`}>
            {draft ? 'Generated' : 'Empty'}
          </span>
        </div>
        {draft ? (
          <>
            <p className="eg-email-card-subject">{draft.subject}</p>
            <p className="eg-email-card-headline">{draft.headline}</p>
          </>
        ) : (
          <p className="eg-email-card-placeholder">Not yet generated</p>
        )}
      </button>
      <div className="eg-email-card-footer">
        <select
          className="eg-layout-select"
          value={layout}
          aria-label={`Layout for Email ${index + 1}`}
          onChange={(e) => onLayoutChange(e.target.value as Layout)}
        >
          {LAYOUTS.map((l) => (
            <option key={l} value={l}>{LAYOUT_LABELS[l]}</option>
          ))}
        </select>
        {draft && (
          <button
            type="button"
            className="eg-copy-btn"
            onClick={() => copy(generateEmailHtml(draft, layout, settings, flow))}
          >
            {copyLabel}
          </button>
        )}
      </div>
    </div>
  );
}

type EmailGridProps = {
  emails: Emails;
  layouts: EmailLayouts;
  settings: Settings;
  generating?: boolean;
  onGenerateAll?: () => void;
  onSelectEmail?: (flow: Flow, index: number) => void;
  onLayoutChange?: (flow: Flow, index: number, layout: Layout) => void;
};

function EmailGrid({
  emails,
  layouts,
  settings,
  generating,
  onGenerateAll,
  onSelectEmail,
  onLayoutChange,
}: EmailGridProps) {
  const [activeFlow, setActiveFlow] = useState<Flow>('replenishment');

  return (
    <div className="eg-grid-section">
      <div className="eg-grid-toolbar">
        <div className="eg-flow-tabs">
          {FLOWS.map((flow) => (
            <button
              key={flow}
              type="button"
              className={`eg-tab ${activeFlow === flow ? 'eg-tab-active' : ''}`}
              onClick={() => setActiveFlow(flow)}
            >
              {FLOW_LABELS[flow]}
            </button>
          ))}
        </div>
        <button
          className="eg-btn-primary"
          type="button"
          onClick={onGenerateAll}
          disabled={!onGenerateAll || generating}
        >
          {generating ? 'Generating…' : 'Generate all'}
        </button>
      </div>
      <div className="eg-email-cards">
        {[0, 1, 2].map((index) => (
          <EmailCard
            key={index}
            index={index}
            draft={emails[activeFlow][index]}
            layout={layouts[activeFlow][index]}
            settings={settings}
            flow={activeFlow}
            onClick={() => onSelectEmail?.(activeFlow, index)}
            onLayoutChange={(layout) => onLayoutChange?.(activeFlow, index, layout)}
          />
        ))}
      </div>
    </div>
  );
}

export default EmailGrid;
